(() => {
  'use strict';
  // Homepage sections slide and fade in as they come into view. Rewritten 4 Oct 2026 for speed: nothing is measured from script
  // (the old version read each section's size with getBoundingClientRect, also inside a ResizeObserver, and every read forced a
  // layout of the whole page, again and again while images loaded) and only transform and opacity animate (the old shadow and
  // outline repainted every frame). Which sections move, the timing, keyboard, print and theme-editor behaviour are unchanged.
  const preference = window.matchMedia('(prefers-reduced-motion: reduce)');
  const compact = window.matchMedia('(max-width: 800px)');
  const tablet = window.matchMedia('(max-width: 1100px)');
  const controllers = new Map();
  const seen = new WeakSet();
  const ease = 'cubic-bezier(0.22, 0.61, 0.36, 1)';

  // hide a section until it comes into view, keeping any inline opacity and pointer-events it had, to put back afterwards
  function hide(root) {
    const original = ['opacity', 'pointer-events'].map(property => [
      property, root.style.getPropertyValue(property), root.style.getPropertyPriority(property)
    ]);
    root.style.opacity = '0';
    root.style.pointerEvents = 'none';
    return () => {
      original.forEach(([property, value, priority]) => {
        if (value) root.style.setProperty(property, value, priority);
        else root.style.removeProperty(property);
      });
    };
  }

  function mount(root) {
    if (controllers.has(root) || seen.has(root) || preference.matches || window.Shopify?.designMode ||
        !window.IntersectionObserver || !Element.prototype.animate ||
        !root.matches('#main > .shopify-section') ||
        root.querySelector('.ol-hero,.ol-intro-strip,.ol-products-section,[data-ol-home-motion="false"]')) return;
    let animation = null;
    let observer = null;
    const reveal = hide(root);
    const done = () => {
      root.removeEventListener('focusin', settle);
      controllers.delete(root);
    };
    // show the section now, without movement (keyboard focus, a section already scrolled past, a hash jump, printing)
    function settle() {
      seen.add(root);
      observer?.disconnect();
      animation?.cancel();
      animation = null;
      reveal();
      done();
    }
    observer = new IntersectionObserver(entries => {
      const entry = entries[entries.length - 1];
      if (!entry.isIntersecting) {
        if (entry.boundingClientRect.bottom <= 0) settle();
        return;
      }
      observer.disconnect();
      seen.add(root);
      if (document.hidden || root.contains(document.activeElement)) {
        settle();
        return;
      }
      const distance = compact.matches ? 42 : tablet.matches ? 58 : 72;
      reveal();
      try {
        animation = root.animate([
          { transform: 'translateY(-' + distance + 'px)', opacity: .75 },
          { transform: 'translateY(0)', opacity: 1 }
        ], { duration: 1200, easing: ease, fill: 'backwards' });
        animation.id = 'ol-home-layer';
        animation.finished.catch(() => {}).finally(() => {
          animation = null;
          done();
        });
      } catch {
        settle();
      }
    }, { rootMargin: '0px 0px -' + Math.min(360, Math.round(window.innerHeight * .6)) + 'px 0px', threshold: 0 });
    observer.observe(root);
    root.addEventListener('focusin', settle);
    controllers.set(root, settle);
  }

  const mountAll = scope => {
    if (!document.body.classList.contains('template-index')) return;
    if (scope.matches?.('#main > .shopify-section')) mount(scope);
    scope.querySelectorAll('#main > .shopify-section').forEach(mount);
  };
  const settleAll = () => [...controllers.values()].forEach(settle => settle());
  window.addEventListener('hashchange', settleAll);
  window.addEventListener('beforeprint', settleAll);
  mountAll(document);
  document.addEventListener('shopify:section:load', event => mountAll(event.target));
  document.addEventListener('shopify:section:unload', event => controllers.get(event.target)?.());
  document.addEventListener('shopify:section:select', event => controllers.get(event.target)?.());
  document.addEventListener('shopify:block:select', event => {
    controllers.get(event.target.closest('.shopify-section'))?.();
  });
  document.addEventListener('keydown', event => {
    if (['Tab', 'ArrowDown', 'ArrowUp', 'PageDown', 'PageUp', 'Home', 'End', ' '].includes(event.key)) settleAll();
  });
  document.addEventListener('visibilitychange', () => {
    if (document.hidden) settleAll();
    else mountAll(document);
  });
  preference.addEventListener('change', () => {
    if (preference.matches) settleAll();
    else mountAll(document);
  });
})();
